/**
 * Preparación de una imagen para usarse como avatar.
 *
 * Un avatar se muestra como mucho a 128 px de lado, así que subir el archivo tal
 * cual sale de la cámara (3–8 MB) no aporta nada y sí estorba. Aquí se reescala
 * a un cuadrado y se recomprime antes de mandarlo.
 *
 * Además hay un motivo duro: el proxy que está frente a Supabase en el VPS de
 * Preview corta el cuerpo de la petición en 1 MB (`client_max_body_size`, el
 * valor por omisión de nginx), aunque el bucket `avatar` acepte 5 MB. Ese 413
 * llega SIN cabeceras CORS, así que el navegador ni siquiera lo puede leer y
 * `fetch` falla con "Failed to fetch". Reescalar deja cualquier foto muy por
 * debajo de ese techo y el problema desaparece, sin depender de la infra.
 *
 * De paso se respeta la orientación EXIF (`imageOrientation: "from-image"`), que
 * es lo que evita que las fotos verticales de iPhone se suban acostadas.
 */

/** Lado del cuadrado final. Suficiente para pantallas retina. */
const LADO = 512;
/** Techo del proxy de Preview. Ver el comentario de arriba. */
export const LIMITE_PROXY = 1024 * 1024;
/** Tope del archivo de ORIGEN: sólo para no reventar la memoria del navegador. */
const MAXIMO_ORIGEN = 20 * 1024 * 1024;

export interface ImagenPreparada {
  blob: Blob;
  tipo: string;
  extension: "webp" | "jpg";
}

const soportaWebp = (() => {
  let cache: boolean | null = null;
  return () => {
    if (cache !== null) return cache;
    try {
      const c = document.createElement("canvas");
      c.width = 1;
      c.height = 1;
      cache = c.toDataURL("image/webp").startsWith("data:image/webp");
    } catch {
      cache = false;
    }
    return cache;
  };
})();

/** Recorta al cuadrado central y escala a `LADO`. */
function dibujarCuadrado(fuente: CanvasImageSource, ancho: number, alto: number): HTMLCanvasElement {
  const lienzo = document.createElement("canvas");
  lienzo.width = LADO;
  lienzo.height = LADO;

  const ctx = lienzo.getContext("2d");
  if (!ctx) throw new Error("Tu navegador no permitió procesar la imagen.");

  // WebP conserva transparencia; JPG no, así que se pinta un fondo blanco para
  // que un PNG recortado no acabe con el fondo en negro.
  if (!soportaWebp()) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, LADO, LADO);
  }

  const lado = Math.min(ancho, alto);
  const x = (ancho - lado) / 2;
  const y = (alto - lado) / 2;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(fuente, x, y, lado, lado, 0, 0, LADO, LADO);
  return lienzo;
}

function aBlob(lienzo: HTMLCanvasElement, tipo: string, calidad: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    lienzo.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("No se pudo convertir la imagen."))),
      tipo,
      calidad,
    );
  });
}

/** Decodifica el archivo respetando la orientación EXIF. */
async function decodificar(file: File): Promise<{ fuente: CanvasImageSource; ancho: number; alto: number }> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { fuente: bitmap, ancho: bitmap.width, alto: bitmap.height };
    } catch {
      // Safari viejo no acepta las opciones: se cae al <img> de abajo.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("El archivo no es una imagen que se pueda abrir."));
      el.src = url;
    });
    return { fuente: img, ancho: img.naturalWidth, alto: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Deja la imagen lista para subir: cuadrada, de `LADO` px y por debajo del techo
 * del proxy. Baja la calidad de forma progresiva sólo si hiciera falta.
 */
export async function prepararAvatar(file: File): Promise<ImagenPreparada> {
  if (file.size > MAXIMO_ORIGEN) {
    throw new Error(
      `La imagen pesa ${(file.size / 1024 / 1024).toFixed(1)} MB y es demasiado grande para procesarla. Usa una de menos de 20 MB.`,
    );
  }

  const { fuente, ancho, alto } = await decodificar(file);
  if (!ancho || !alto) throw new Error("El archivo no es una imagen válida.");

  const lienzo = dibujarCuadrado(fuente, ancho, alto);
  if ("close" in fuente && typeof fuente.close === "function") fuente.close();

  const webp = soportaWebp();
  const tipo = webp ? "image/webp" : "image/jpeg";
  const extension: "webp" | "jpg" = webp ? "webp" : "jpg";

  // A 512 px la primera calidad ya suele dar ~40 KB; el bucle es sólo una red
  // de seguridad para imágenes con mucho ruido.
  for (const calidad of [0.85, 0.7, 0.55]) {
    const blob = await aBlob(lienzo, tipo, calidad);
    if (blob.size <= LIMITE_PROXY) return { blob, tipo, extension };
  }

  throw new Error("No se pudo comprimir la imagen lo suficiente. Prueba con otra.");
}
